import { all, call, getContext, put, race, select, take, takeEvery } from '@redux-saga/core/effects';
import { push } from 'connected-react-router';
import type { AxiosResponse } from 'axios';
import type { ActionCreatorWithoutPayload } from '@reduxjs/toolkit';
import i18n from '../../../i18n';
import type { IAttachmentRead, IEmployee, IMessage, ISecureInboxTopic, ISecureInboxSettings, IUserInfo, IUserMailbox } from '../../api/domain';
import { AttachmentOriginType } from '../../api/domain';
import type { I18ImportService, SecureInboxApiService } from '../../api/services';
import { NotificationType } from '../../enum';
import { addNotification, hideProgressIndicator, showProgressIndicator } from '../application/application-actions';
import { fetchEmployees, fetchEmployeesFailure, fetchEmployeesSuccess } from '../employee-master-data-api/employee-master-data-actions';
import { logError, logTrace } from '../logging-api/logging-api-actions';
import { fetchDocumentsByIds } from '../secure-document-store-api/secure-document-store-api-actions';
import { getUserInfo } from '../user-info-api/user-info-api-selectors';
import {
    archiveMessage,
    archiveMessageFailure,
    archiveMessageSuccess,
    archiveMultipleMessages,
    archiveMultipleMessagesFailure,
    archiveMultipleMessagesSuccess,
    deleteAttachment,
    deleteAttachmentSuccess,
    deleteMessage,
    deleteMessageFailure,
    deleteMessageSuccess,
    deleteMultipleMessages,
    deleteMultipleMessagesFailure,
    deleteMultipleMessagesSuccess,
    downloadAttachment,
    downloadAttachmentFailure,
    downloadAttachmentSuccess,
    fetchAttachmentContent,
    fetchAttachmentContentFailure,
    fetchAttachmentContentSuccess,
    fetchAttachmentsSuccess,
    fetchMailboxes,
    fetchMailboxesFailure,
    fetchMailboxesSuccess,
    fetchMessage,
    fetchMessageFailure,
    fetchMessages,
    fetchMessagesFailure,
    fetchMessagesSuccess,
    fetchMessageSuccess,
    fetchSettings,
    fetchSettingsFailure,
    fetchSettingsSuccess,
    fetchTopics,
    fetchTopicsFailure,
    fetchTopicsSuccess,
    fetchUnreadCount,
    fetchUnreadCountFailure,
    fetchUnreadCountSuccess,
    queueMessage,
    readMessage,
    readMessageFailure,
    readMessageSuccess,
    resolveMessagesAttachments,
    resolveRmForMailboxes,
    resolveRmForMailboxesFailure,
    resolveRmForMailboxesSuccess,
    sendMessage,
    sendMessageFailure,
    sendMessageOutsideInbox,
    sendMessageSuccess,
    unarchiveMessage,
    unarchiveMessageFailure,
    unarchiveMessageSuccess,
    unarchiveMultipleMessages,
    unarchiveMultipleMessagesFailure,
    unarchiveMultipleMessagesSuccess,
    uploadAttachment,
    uploadAttachmentFailure,
    uploadAttachmentSuccess,
} from './secure-inbox-api-actions';
import { getAttachments, getMailboxes } from './secure-inbox-api-selectors';
import de from './translations/de.json';
import en from './translations/en.json';
import fr from './translations/fr.json';
import it from './translations/it.json';

function* addTranslations() {
    i18n.addResources('de', de);
    i18n.addResources('en', en);
    i18n.addResources('fr', fr);
    i18n.addResources('it', it);

    yield;
}

function* handleFetchSettings() {
    const api: SecureInboxApiService = yield getContext('secureInboxApiService');
    try {
        const settings: ISecureInboxSettings = yield call([api, api.fetchSettings]);
        yield put(fetchSettingsSuccess(settings));
    } catch (error: unknown) {
        yield put(logError(`could not fetch settings: ${error}`));
        yield put(fetchSettingsFailure());
    }
}

function* handleFetchTopics() {
    const api: SecureInboxApiService = yield getContext('secureInboxApiService');
    try {
        const topics: ISecureInboxTopic[] = yield call([api, api.fetchTopics]);
        yield put(fetchTopicsSuccess(topics));
    } catch (error: unknown) {
        yield put(logError(`could not fetch topics: ${error}`));
        yield put(fetchTopicsFailure());
    }
}

function* handleFetchTopicsSuccess(action: ReturnType<typeof fetchTopicsSuccess>) {
    const { payload: topics } = action;
    const i18nImportService: I18ImportService = yield getContext('i18ImportService');
    i18nImportService.importTranslations({ 'secureinbox.topics': topics });
}

function* handleFetchMessages(action: ReturnType<typeof fetchMessages>) {
    const { payload: messageParams } = action;
    try {
        const secureInboxApi: SecureInboxApiService = yield getContext('secureInboxApiService');
        yield put(showProgressIndicator());
        const { messages, totalCount } = yield call([secureInboxApi, secureInboxApi.fetchMessages], messageParams);
        yield put(fetchMessagesSuccess({ messages, totalCount }));
    } catch (error: unknown) {
        yield put(logError(`could not fetch messages: ${error}`));
        yield put(fetchMessagesFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleFetchMessage(action: ReturnType<typeof fetchMessage>) {
    const { messageId } = action.payload;
    try {
        const secureInboxApi: SecureInboxApiService = yield getContext('secureInboxApiService');
        yield put(showProgressIndicator());
        const message: IMessage = yield call([secureInboxApi, secureInboxApi.fetchMessage], messageId);
        yield put(fetchMessageSuccess(message));
    } catch (error: unknown) {
        yield put(logError(`could not fetch message ${messageId}: ${error}`));
        yield put(fetchMessageFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleFetchMessageSuccess({ payload: message }: ReturnType<typeof fetchMessageSuccess>) {
    yield put(resolveMessagesAttachments({ messages: [message] }));
}

function* handleFetchUnreadCount() {
    const api: SecureInboxApiService = yield getContext('secureInboxApiService');
    try {
        const mailboxes: IUserMailbox[] = yield select(getMailboxes);
        const unreadCounts: number[] = yield all(mailboxes.map((mailbox) => call([api, api.fetchUnreadCount], mailbox.id)));
        yield put(fetchUnreadCountSuccess(unreadCounts.reduce((acc, val) => val + acc, 0)));
    } catch (error: unknown) {
        yield put(logError(`could not fetch unread count: ${error}`));
        yield put(fetchUnreadCountFailure());
    }
}

function* handleUploadAttachment(action: ReturnType<typeof uploadAttachment>) {
    const { attachmentContent, attachmentName, messageId } = action.payload;
    try {
        const api: SecureInboxApiService = yield getContext('secureInboxApiService');
        yield call([api, api.uploadAttachment], messageId, attachmentContent, attachmentName);
        yield put(uploadAttachmentSuccess());
    } catch (error: unknown) {
        yield put(logError(`could not upload attachment: ${error}`));
        yield put(uploadAttachmentFailure());
    }
}

function* handleFetchAttachmentContent(action: ReturnType<typeof fetchAttachmentContent>) {
    try {
        const { attachmentId } = action.payload;
        const api: SecureInboxApiService = yield getContext('secureInboxApiService');
        const fileContent: string = yield call([api, api.fetchAttachmentContent], attachmentId);
        yield put(fetchAttachmentContentSuccess({ attachmentId, fileSize: new Blob([fileContent]).size }));
    } catch (error: unknown) {
        yield put(logError(`could not fetch attachment content: ${error}`));
        yield put(fetchAttachmentContentFailure());
    }
}

function* handleSendMessage(action: ReturnType<typeof sendMessage>) {
    const {
        payload: { attachmentsToBeUploaded, mailboxId, newMessage },
    } = action;
    try {
        const api: SecureInboxApiService = yield getContext('secureInboxApiService');
        yield put(showProgressIndicator());
        const { headers }: AxiosResponse = yield call([api, api.sendMessage], mailboxId, newMessage);
        const messageId: string | undefined = /\/([\w-]+)$/.exec((headers as { location: string }).location)?.[1];
        if (!messageId) {
            throw new Error('messageId is undefined');
        }
        let hasFailure = false;
        for (const file of attachmentsToBeUploaded) {
            yield put(uploadAttachment({ attachmentContent: file, attachmentName: file.name, messageId }));
            const uploadAction: ActionCreatorWithoutPayload = yield take([uploadAttachmentSuccess, uploadAttachmentFailure]);
            if (uploadAction.type === uploadAttachmentFailure.toString()) {
                hasFailure = true;
                break;
            }
        }
        if (!hasFailure) {
            yield put(queueMessage({ messageId }));
        }
    } catch (error: unknown) {
        yield put(logError(`could not send message: ${error}`));
        yield put(sendMessageFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleQueueMessage(action: ReturnType<typeof queueMessage>) {
    const {
        payload: { messageId },
    } = action;
    try {
        const api: SecureInboxApiService = yield getContext('secureInboxApiService');
        yield call([api, api.queueMessage], messageId);
        yield put(sendMessageSuccess());
    } catch (error: unknown) {
        yield put(logError(`could not send message: ${error}`));
        yield put(sendMessageFailure());
    }
}

function* handleSendMessageOutsideInbox(action: ReturnType<typeof sendMessageOutsideInbox>) {
    const api: SecureInboxApiService = yield getContext('secureInboxApiService');
    const {
        payload: { newMessage, mailboxId },
    } = action;
    try {
        yield put(showProgressIndicator());
        const { headers }: AxiosResponse = yield call([api, api.sendMessage], mailboxId, newMessage);
        const messageId: string | undefined = /\/([\w-]+)$/.exec((headers as { location: string }).location)?.[1];
        if (!messageId) {
            throw new Error('messageId is undefined');
        }
        yield call([api, api.queueMessage], messageId);
    } catch (error: unknown) {
        yield put(logError(`could not send message: ${error}`));
        yield put(sendMessageFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleUploadAttachmentFailure() {
    yield put(
        addNotification({
            title: i18n.t('secure-inbox-api.error.upload-attachments'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleMessageSendFailure() {
    yield put(
        addNotification({
            title: i18n.t('secure-inbox-api.error.send-message'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleMessageSendSuccess() {
    yield put(push(i18n.t('routes.secure-inbox')));
    yield put(
        addNotification({
            title: i18n.t('secure-inbox-api.success.send-message'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleFetchMailboxes({ payload: externalIds = [] }: ReturnType<typeof fetchMailboxes>) {
    const api: SecureInboxApiService = yield getContext('secureInboxApiService');
    try {
        const userInfo: IUserInfo | undefined = yield select(getUserInfo);
        if (!externalIds.length && userInfo) {
            // FTT-2614 required to fix some internal CHSU error when fetching mailboxes
            externalIds.push(userInfo.loginId);
        }
        const mailboxes: IUserMailbox[] = yield call([api, api.fetchMailboxes], externalIds);
        yield put(fetchMailboxesSuccess(mailboxes));
        yield put(fetchUnreadCount());
    } catch (error: unknown) {
        yield put(logError(`could not fetch mailboxes: ${error}`));
        yield put(fetchMailboxesFailure());
    }
}

function* handleResolveRmForMailboxes() {
    try {
        const mailboxes: IUserMailbox[] = yield select(getMailboxes);
        const stripRmPrefix = (toBeStrippedOf?: string) => toBeStrippedOf && toBeStrippedOf.replace(/^(rm|RM)/, '');
        const rmMailboxExternalIds = mailboxes
            .map((mailbox) => stripRmPrefix(mailbox.rmMailboxExternalId))
            .reduce<string[]>((acc, val) => (val ? acc.concat(val) : acc), []);
        yield put(fetchEmployees(rmMailboxExternalIds));
        const { success, failure } = yield race({
            success: take(fetchEmployeesSuccess),
            failure: take(fetchEmployeesFailure),
        });
        if (failure) {
            throw new Error('Fetching the employees by object nr failed');
        }
        const employees: IEmployee[] = success.payload;
        yield put(
            resolveRmForMailboxesSuccess(
                mailboxes.map((mailbox) => ({
                    ...mailbox,
                    rmExists: employees.some((employee) => employee.objectNumber === stripRmPrefix(mailbox.rmMailboxExternalId)),
                })),
            ),
        );
    } catch (error: unknown) {
        yield put(logError(`could not resolve rm for mailboxes: ${error}`));
        yield put(resolveRmForMailboxesFailure());
    }
}

function* handleFetchMailboxesSuccess() {
    yield put(fetchUnreadCount());
    yield put(resolveRmForMailboxes());
}

function* handleResolveMessagesAttachments({ payload: { messages } }: ReturnType<typeof resolveMessagesAttachments>) {
    const secureInboxApi: SecureInboxApiService = yield getContext('secureInboxApiService');
    if (messages) {
        for (const message of messages) {
            if (message.attachmentIds) {
                const documentStoreDocumentsBatchBuffer: string[] = [];
                const inboxAttachmentsReferenceBatchBuffer: string[] = [];
                for (const attachmentRead of message.attachmentIds) {
                    if (attachmentRead.origin === AttachmentOriginType.SECURE_INBOX) {
                        inboxAttachmentsReferenceBatchBuffer.push(message.id);
                    } else if (attachmentRead.origin === AttachmentOriginType.SECURE_DOCUMENT_STORE) {
                        documentStoreDocumentsBatchBuffer.push(attachmentRead.attachmentId);
                    } else {
                        throw new Error(`Invalid AttachmentOriginType: ${attachmentRead?.origin}`);
                    }
                }

                // Load attachments from secure-inbox by messageId
                if (inboxAttachmentsReferenceBatchBuffer.length) {
                    const attachments: IAttachmentRead[] = yield call([secureInboxApi, secureInboxApi.fetchAttachments], inboxAttachmentsReferenceBatchBuffer);
                    yield put(fetchAttachmentsSuccess(attachments));
                    for (const attachment of attachments) {
                        yield put(fetchAttachmentContent({ attachmentId: attachment.id }));
                    }
                }

                // Load attachments from secure-document-store by attachmentId
                if (documentStoreDocumentsBatchBuffer.length) {
                    yield put(fetchDocumentsByIds(documentStoreDocumentsBatchBuffer));
                }
            }
        }
    }
}

function* handleFetchMessagesSuccess({ payload: { messages } }: ReturnType<typeof fetchMessagesSuccess>) {
    yield put(fetchUnreadCount());
    yield put(resolveMessagesAttachments({ messages }));
}

function* handleArchiveMessage(action: ReturnType<typeof archiveMessage>) {
    const api: SecureInboxApiService = yield getContext('secureInboxApiService');
    const { message, params } = action.payload;
    try {
        yield put(showProgressIndicator());
        yield call([api, api.archiveMessage], message.id);
        yield put(archiveMessageSuccess(params));
    } catch (error: unknown) {
        yield put(logError(`could not archive message: ${error}`));
        yield put(archiveMessageFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleDownloadAttachment(action: ReturnType<typeof downloadAttachment>) {
    const { attachmentId } = action.payload;
    try {
        yield put(logTrace(`downloading attachment with id ${attachmentId}`));
        const api: SecureInboxApiService = yield getContext('secureInboxApiService');
        const attachments: IAttachmentRead[] = yield select(getAttachments);
        const attachment: IAttachmentRead = attachments.find((att) => att.id === attachmentId) || (yield call([api, api.fetchAttachment], attachmentId));
        if (!attachment) {
            throw new Error(`could not find attachment with id ${attachmentId}`);
        }
        const blobWithMimeType: Blob = yield call([api, api.fetchAttachmentContent], attachmentId);
        const link = document.createElement('a');
        const url = URL.createObjectURL(blobWithMimeType);
        link.href = url;
        link.download = attachment.fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        yield put(downloadAttachmentSuccess());
    } catch (error: unknown) {
        yield put(logError(`could not download attachment with id ${attachmentId}: ${error}`));
        yield put(downloadAttachmentFailure());
    }
}

function* handleReadMessage(action: ReturnType<typeof readMessage>) {
    const api: SecureInboxApiService = yield getContext('secureInboxApiService');
    const message = action.payload;
    try {
        yield call([api, api.markAsRead], message.id);
        yield put(readMessageSuccess());
    } catch (error: unknown) {
        yield put(logError(`could not flag message as read: ${error}`));
        yield put(readMessageFailure());
    }
}

function* handleReadMessageSuccess() {
    yield put(fetchUnreadCount());
}

function* handleUnarchiveMessage(action: ReturnType<typeof unarchiveMessage>) {
    const api: SecureInboxApiService = yield getContext('secureInboxApiService');
    const { message, params } = action.payload;
    try {
        yield put(showProgressIndicator());
        yield call([api, api.unarchiveMessage], message.id);
        yield put(unarchiveMessageSuccess(params));
    } catch (error: unknown) {
        yield put(logError(`could not unarchive message: ${error}`));
        yield put(unarchiveMessageFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleArchiveMultipleMessages(action: ReturnType<typeof archiveMultipleMessages>) {
    const api: SecureInboxApiService = yield getContext('secureInboxApiService');
    const { messages, params } = action.payload;
    try {
        yield put(showProgressIndicator());
        yield all(messages.map((message) => call([api, api.archiveMessage], message.id)));
        yield put(archiveMultipleMessagesSuccess(params));
    } catch (error: unknown) {
        yield put(logError(`could not archive messages: ${error}`));
        yield put(archiveMultipleMessagesFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleUnarchiveMultipleMessages(action: ReturnType<typeof unarchiveMultipleMessages>) {
    const api: SecureInboxApiService = yield getContext('secureInboxApiService');
    const { messages, params } = action.payload;
    try {
        yield put(showProgressIndicator());
        yield all(messages.map((message) => call([api, api.unarchiveMessage], message.id)));
        yield put(unarchiveMultipleMessagesSuccess(params));
    } catch (error: unknown) {
        yield put(logError(`could not unarchive messages: ${error}`));
        yield put(unarchiveMultipleMessagesFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleArchiveMessageSuccess(action: ReturnType<typeof archiveMessageSuccess>) {
    yield put(fetchMessages(action.payload));
    yield put(
        addNotification({
            title: i18n.t('secure-inbox-api.success.archive-message'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleArchiveMessageFailure() {
    yield put(
        addNotification({
            title: i18n.t('secure-inbox-api.error.archive-message'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleUnarchiveMessageSuccess(action: ReturnType<typeof unarchiveMessageSuccess>) {
    yield put(fetchMessages(action.payload));
    yield put(
        addNotification({
            title: i18n.t('secure-inbox-api.success.unarchive-message'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleUnarchiveMessageFailure() {
    yield put(
        addNotification({
            title: i18n.t('secure-inbox-api.error.unarchive-message'),
            type: NotificationType.ERROR,
        }),
    );
}

function* handleDeleteMessage(action: ReturnType<typeof deleteMessage>) {
    const api: SecureInboxApiService = yield getContext('secureInboxApiService');
    const { message, params } = action.payload;
    try {
        yield put(showProgressIndicator());
        yield call([api, api.deleteMessage], message.id);
        yield put(deleteMessageSuccess({ attachmentIds: message.attachmentIds, messageId: message.id, params }));
    } catch (error: unknown) {
        yield put(logError(`could not delete message: ${error}`));
        yield put(deleteMessageFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleDeleteMultipleMessages(action: ReturnType<typeof deleteMultipleMessages>) {
    const api: SecureInboxApiService = yield getContext('secureInboxApiService');
    const { messages, params } = action.payload;
    try {
        yield put(showProgressIndicator());
        yield all(messages.map((message) => call([api, api.deleteMessage], message.id)));
        yield put(deleteMultipleMessagesSuccess({ messages, params }));
    } catch (error: unknown) {
        yield put(logError(`could not delete messages: ${error}`));
        yield put(deleteMultipleMessagesFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleDeleteAttachment(action: ReturnType<typeof deleteAttachment>) {
    try {
        const api: SecureInboxApiService = yield getContext('secureInboxApiService');
        const { attachmentId, messageId } = action.payload;
        yield put(showProgressIndicator());
        yield call([api, api.deleteAttachment], attachmentId);
        yield put(deleteAttachmentSuccess({ attachmentId, messageId }));
    } catch (error: unknown) {
        yield put(logError(`could not delete attachment: ${error}`));
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleDeleteMessageSuccess(action: ReturnType<typeof deleteMessageSuccess>) {
    const { attachmentIds, messageId, params } = action.payload;
    if (attachmentIds) {
        for (const attachmentIdRead of attachmentIds) {
            if (attachmentIdRead.origin === AttachmentOriginType.SECURE_INBOX) {
                yield put(deleteAttachment({ attachmentId: attachmentIdRead.attachmentId, messageId }));
            }
            // We do not delete secure inbox messages because of legacy reasons
        }
    }
    yield put(fetchMessages(params));
    yield put(
        addNotification({
            title: i18n.t('secure-inbox-api.success.delete-message'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleDeleteMessagesSuccess(action: ReturnType<typeof deleteMultipleMessagesSuccess>) {
    const { messages, params } = action.payload;
    for (const message of messages) {
        if (message.attachmentIds) {
            for (const attachmentIdRead of message.attachmentIds) {
                if (attachmentIdRead.origin === AttachmentOriginType.SECURE_INBOX) {
                    yield put(deleteAttachment({ attachmentId: attachmentIdRead.attachmentId, messageId: message.id }));
                }
                // We do not delete sds attachments because of legacy reasons
            }
        }
    }
    yield put(fetchMessages(params));
    yield put(
        addNotification({
            title: i18n.t('secure-inbox-api.success.delete-message'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleDeleteMessageFailure() {
    yield put(
        addNotification({
            title: i18n.t('secure-inbox-api.error.delete-message'),
            type: NotificationType.ERROR,
        }),
    );
}

export function* secureInboxApiSaga() {
    yield all([
        call(addTranslations),
        takeEvery(archiveMessage, handleArchiveMessage),
        takeEvery(archiveMessageFailure, handleArchiveMessageFailure),
        takeEvery(archiveMessageSuccess, handleArchiveMessageSuccess),
        takeEvery(archiveMultipleMessages, handleArchiveMultipleMessages),
        takeEvery(archiveMultipleMessagesFailure, handleArchiveMessageFailure),
        takeEvery(archiveMultipleMessagesSuccess, handleArchiveMessageSuccess),
        takeEvery(deleteAttachment, handleDeleteAttachment),
        takeEvery(deleteMessage, handleDeleteMessage),
        takeEvery(deleteMessageFailure, handleDeleteMessageFailure),
        takeEvery(deleteMessageSuccess, handleDeleteMessageSuccess),
        takeEvery(deleteMultipleMessages, handleDeleteMultipleMessages),
        takeEvery(deleteMultipleMessagesFailure, handleDeleteMessageFailure),
        takeEvery(deleteMultipleMessagesSuccess, handleDeleteMessagesSuccess),
        takeEvery(downloadAttachment, handleDownloadAttachment),
        takeEvery(fetchAttachmentContent, handleFetchAttachmentContent),
        takeEvery(fetchMailboxes, handleFetchMailboxes),
        takeEvery(fetchMailboxesSuccess, handleFetchMailboxesSuccess),
        takeEvery(fetchMessage, handleFetchMessage),
        takeEvery(fetchMessageSuccess, handleFetchMessageSuccess),
        takeEvery(fetchMessages, handleFetchMessages),
        takeEvery(fetchMessagesSuccess, handleFetchMessagesSuccess),
        takeEvery(fetchSettings, handleFetchSettings),
        takeEvery(fetchTopics, handleFetchTopics),
        takeEvery(fetchTopicsSuccess, handleFetchTopicsSuccess),
        takeEvery(fetchUnreadCount, handleFetchUnreadCount),
        takeEvery(queueMessage, handleQueueMessage),
        takeEvery(readMessage, handleReadMessage),
        takeEvery(readMessageSuccess, handleReadMessageSuccess),
        takeEvery(resolveMessagesAttachments, handleResolveMessagesAttachments),
        takeEvery(resolveRmForMailboxes, handleResolveRmForMailboxes),
        takeEvery(sendMessage, handleSendMessage),
        takeEvery(sendMessageFailure, handleMessageSendFailure),
        takeEvery(sendMessageOutsideInbox, handleSendMessageOutsideInbox),
        takeEvery(sendMessageSuccess, handleMessageSendSuccess),
        takeEvery(unarchiveMessage, handleUnarchiveMessage),
        takeEvery(unarchiveMessageFailure, handleUnarchiveMessageFailure),
        takeEvery(unarchiveMessageSuccess, handleUnarchiveMessageSuccess),
        takeEvery(unarchiveMultipleMessages, handleUnarchiveMultipleMessages),
        takeEvery(unarchiveMultipleMessagesFailure, handleUnarchiveMessageFailure),
        takeEvery(unarchiveMultipleMessagesSuccess, handleUnarchiveMessageSuccess),
        takeEvery(uploadAttachment, handleUploadAttachment),
        takeEvery(uploadAttachmentFailure, handleUploadAttachmentFailure),
    ]);
}
